// Task #3694 — DTO risposta condivisi per gli endpoint Calendario.
// SoT unica BE↔FE: il backend (calendar.service / hr.service) li usa come tipi
// di ritorno, il FE come generic di `apiClient.get<T>`. Tutti gli endpoint
// Calendar usano l'envelope `{ success, data }`: qui modelliamo SOLO il payload
// `data`. Vedere docs/zod-boundary.md § Risposte e lo stile di crm.ts.
//
// Convenzioni wire (Task #3694):
//  - timestamp/timestamptz pg → z.union([z.string(), z.date()]) (wire = ISO
//    string; Date per riuso BE pre-serializzazione, come crm.ts).
//  - COUNT(*)/SUM senza ::int → z.union([z.string(), z.number()]) (pg numeric =
//    stringa sul wire node-postgres); COUNT(...)::int → z.number().
//  - colonne NULLABLE in DB o alias da LEFT JOIN → .nullable().
//  - jsonb generici → z.unknown() (o shape se documentata).
//  - righe da `SELECT alias.*` → colonne note enumerate + .passthrough().

import { z } from 'zod';
export * from './calendar-automation';

/** Effective authority resolved by the existing Calendar bootstrap. */
export const calendarAccessSchema = z.object({
  canManage: z.boolean(),
  isBroad: z.boolean(),
  isAdmin: z.boolean(),
  /** null means unrestricted; sharing is already resolved server-side per action. */
  createUserIds: z.array(z.string()).nullable().optional(),
  editUserIds: z.array(z.string()).nullable().optional(),
  deleteUserIds: z.array(z.string()).nullable().optional(),
});
export type CalendarAccess = z.infer<typeof calendarAccessSchema>;

// Service invariant, deliberately separate from the HTTP patch and the DB row.
// Every writer validates the resulting state, including a one-ended update or
// reactivation. This is not a new request envelope or an override authorizer.
export const calendarAppointmentStatusSchema = z.enum([
  'scheduled', 'confirmed', 'reminded', 'rescheduled', 'cancelled', 'completed', 'no_show',
]);
const calendarInstantSchema = z.union([z.date(), z.string().min(1)])
  .transform(value => new Date(value))
  .refine(value => Number.isFinite(value.getTime()), 'Istante non valido');
export const calendarAppointmentIntervalSchema = z.object({
  start_time: calendarInstantSchema,
  end_time: calendarInstantSchema,
  status: calendarAppointmentStatusSchema,
}).refine(value => value.end_time.getTime() > value.start_time.getTime(), {
  message: 'La fine deve essere successiva all’inizio', path: ['end_time'],
});
export const calendarAppointmentMetadataSchema = z.object({
  overrides: z.object({
    force_outside_shift: z.boolean().optional(),
    force_without_clockin: z.boolean().optional(),
    force_missing_skills: z.boolean().optional(),
    reason: z.string().nullable().optional(),
    applied_by: z.string().nullable().optional(),
    applied_at: z.string().optional(),
    missing_skills: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  }).passthrough().optional(),
  product_id: z.string().nullable().optional(),
  source: z.string().optional(),
  source_detail: z.record(z.unknown()).optional(),
}).passthrough();

// ============================================================================
// 1) Appuntamento — GET /calendar/appointments[/...], /calendar/appointments/:id
// ============================================================================
// Riga `SELECT a.*` dalla tabella `appointments` (schema.sql:7391) + alias
// joinati (type_name/type_color, user_name/user_email, assigned_to_name,
// campaign_name, store_name, form_slug/form_name — calendar.service:732-744).
// Chiusa con .passthrough() per tollerare colonne/alias futuri.
export const calendarAppointmentSchema = z
  .object({
    // --- colonne tabella appointments (a.*) ---
    id: z.string(),
    tenant_id: z.string(),
    // user_id è NULLABLE in DB (appointment "orfano"/AI senza owner).
    user_id: z.string().nullable(),
    service_type_id: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    // timestamptz → ISO string sul wire, Date lato service.
    start_time: z.union([z.string(), z.date()]),
    end_time: z.union([z.string(), z.date()]),
    status: z.string(),
    location: z.string().nullable(),
    meeting_link: z.string().nullable(),
    customer_name: z.string().nullable(),
    customer_email: z.string().nullable(),
    customer_phone: z.string().nullable(),
    customer_id: z.string().nullable(),
    notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
    reminder_sent: z.boolean().nullable(),
    google_event_id: z.string().nullable(),
    google_calendar_id: z.string().nullable(),
    google_sync_status: z.string().nullable(),
    google_sync_error: z.string().nullable(),
    // jsonb DEFAULT '{}' — shape libera (priority, source, ecc. dentro metadata).
    metadata: z.unknown(),
    created_by: z.string().nullable(),
    created_at: z.union([z.string(), z.date()]),
    updated_at: z.union([z.string(), z.date()]),
    lead_id: z.string().nullable(),
    assigned_to_id: z.string().nullable(),
    // uuid[] DEFAULT '{}' — array di observer (mai null in pratica, ma tolleriamo).
    observer_ids: z.array(z.string()).nullable(),
    campaign_id: z.string().nullable(),
    // store_id è NOT NULL in DB (Task #2723 recinto sede).
    store_id: z.string(),
    google_account_email: z.string().nullable(),
    appointment_form_id: z.string().nullable(),
    cancel_token_hash: z.string().nullable(),
    // Task #595 — tassonomia sorgente (popolata da trigger, può essere null).
    source_kind: z.string().nullable(),
    // Task #... — identificatore logico persona (non FK).
    person_id: z.string().nullable(),
    privacy_document_id: z.string().nullable(),
    privacy_document_version: z.number().nullable(),
    company_id: z.string().nullable(),
    // --- alias joinati (LEFT JOIN → nullable) ---
    type_name: z.string().nullable(), // pos_service_types.name
    type_color: z.string().nullable(), // pos_service_types.color
    user_name: z.string().nullable(), // CONCAT(u.first_name,' ',u.last_name)
    user_email: z.string().nullable(), // users.email owner
    assigned_to_name: z.string().nullable(), // CONCAT(au.first_name,' ',au.last_name)
    campaign_name: z.string().nullable(), // crm_campaigns.name
    store_name: z.string().nullable(), // stores.name
    form_slug: z.string().nullable(), // appointment_forms.slug
    form_name: z.string().nullable(), // appointment_forms.name
  })
  .passthrough();
export type CalendarAppointment = z.infer<typeof calendarAppointmentSchema>;

/** Feed projection of canonical appointments; wire timestamps are always ISO. */
export const calendarFeedEventRowSchema = calendarAppointmentSchema.pick({
  id: true, title: true, start_time: true, end_time: true, status: true, location: true,
});
export const calendarFeedEventSchema = z.object({
  id: z.string(), title: z.string(),
  start: z.string().datetime(), end: z.string().datetime(),
  status: z.string(), location: z.string().nullable(),
});
export type CalendarFeedEvent = z.infer<typeof calendarFeedEventSchema>;
export const calendarFeedEventsResponseSchema = z.object({
  success: z.literal(true), data: z.array(calendarFeedEventSchema),
});
export function toCalendarFeedEvent(input: unknown): CalendarFeedEvent {
  const row = calendarFeedEventRowSchema.parse(input);
  return calendarFeedEventSchema.parse({
    id: row.id, title: row.title,
    start: new Date(row.start_time).toISOString(),
    end: new Date(row.end_time).toISOString(),
    status: row.status, location: row.location,
  });
}

// ============================================================================
// 2) Impostazioni calendario — GET /calendar/settings
// ============================================================================
// `SELECT * FROM calendar_settings` (calendar.service:334, schema.sql:8752).
// L'endpoint può ritornare `data: null` se il tenant non ha ancora un record:
// quel null è modellato dal caller/bootstrap, qui descriviamo la riga presente.
export const calendarSettingsRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    slot_duration: z.number(),
    // time without time zone → stringa 'HH:MM:SS' sul wire.
    working_hours_start: z.string(),
    working_hours_end: z.string(),
    break_start: z.string().nullable(), // colonna nullable in DB
    break_end: z.string().nullable(),
    // integer[] NOT NULL — giorni disponibili (0-6).
    days_available: z.array(z.number()),
    timezone: z.string(),
    buffer_before: z.number().nullable(),
    buffer_after: z.number().nullable(),
    max_advance_booking: z.number().nullable(),
    min_advance_booking: z.number().nullable(),
    allow_cancellation: z.boolean().nullable(),
    cancellation_deadline: z.number().nullable(),
    google_calendar_sync: z.boolean().nullable(),
    // colonna DB reale: default_appointment_type (nullable).
    default_appointment_type: z.string().nullable(),
    created_at: z.union([z.string(), z.date()]),
    updated_at: z.union([z.string(), z.date()]),
  })
  .passthrough();
export type CalendarSettingsRow = z.infer<typeof calendarSettingsRowSchema>;

// Writes are deliberately separate from DB rows (no ids/timestamps or unknown
// columns). Integer minutes match the real calendar_settings columns.
export const calendarSettingsTimingSchema = z.object({
  slot_duration: z.number().finite().int().positive('La durata dello slot deve essere positiva'),
  buffer_before: z.number().finite().int().nonnegative('Il buffer prima non può essere negativo'),
  buffer_after: z.number().finite().int().nonnegative('Il buffer dopo non può essere negativo'),
});
const calendarTimeSchema = z.string().regex(
  /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/,
  'Orario non valido (HH:MM)',
);
export const calendarSettingsInputSchema = calendarSettingsTimingSchema.extend({
  working_hours_start: calendarTimeSchema,
  working_hours_end: calendarTimeSchema,
  break_start: calendarTimeSchema.nullable(),
  break_end: calendarTimeSchema.nullable(),
  days_available: z.array(z.number().int().min(1).max(7)),
  timezone: z.string().min(1).max(50).refine((value) => {
    try { new Intl.DateTimeFormat('en', { timeZone: value }); return true; }
    catch { return false; }
  }, 'Fuso orario non valido'),
  max_advance_booking: z.number().finite().int().nonnegative().nullable(),
  min_advance_booking: z.number().finite().int().nonnegative().nullable(),
  allow_cancellation: z.boolean().nullable(),
  cancellation_deadline: z.number().finite().int().nonnegative().nullable(),
  google_calendar_sync: z.boolean().nullable(),
  default_appointment_type: z.string().max(100).nullable(),
}).partial().strict();
export type CalendarSettingsInput = z.infer<typeof calendarSettingsInputSchema>;

// ============================================================================
// 3) Time slot — GET /calendar/slots/:date (e /api/v2/calendar/availability)
// ============================================================================
// Interfaccia TimeSlot (calendar.service:318-328): start/end ISO string,
// available boolean, appointments = tutti gli appuntamenti che intersecano lo
// slot (array vuoto se libero).
export const calendarTimeSlotSchema = z.object({
  start: z.string(), // ISO string
  end: z.string(), // ISO string
  available: z.boolean(),
  appointments: z.array(calendarAppointmentSchema),
});
export type CalendarTimeSlot = z.infer<typeof calendarTimeSlotSchema>;

// ============================================================================
// 4) Tipo servizio (durata-tag) — GET /calendar/types
// ============================================================================
// `SELECT * FROM pos_service_types` (calendar.service:420, schema.sql:18596)
// arricchito lato service con `linked_services` e `linked_services_count`
// (calendar.service:453-457). .passthrough() per il resto di linked_services.
export const calendarServiceTypeSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    duration: z.number(), // minuti (integer DB)
    color: z.string().nullable(), // color ha DEFAULT ma è nullable in DB
    is_active: z.boolean().nullable(),
    requires_approval: z.boolean().nullable(),
    created_at: z.union([z.string(), z.date()]),
    updated_at: z.union([z.string(), z.date()]),
    // Aggiunti dal service (JS array length): sempre numerico.
    linked_services_count: z.number().optional(),
    // Servizi collegati risolti dal resolver POS (shape libera qui).
    linked_services: z.array(z.unknown()).optional(),
  })
  .passthrough();
export type CalendarServiceType = z.infer<typeof calendarServiceTypeSchema>;

// ============================================================================
// 5) Utente calendario — GET /calendar/users
// ============================================================================
// SQL: SELECT id, first_name, last_name, CONCAT(...) AS name, email, role
// FROM users WHERE ... (calendar.service:2131). Tutti NOT NULL nel SELECT.
export const calendarUserSchema = z.object({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  name: z.string(), // CONCAT(first_name,' ',last_name)
  email: z.string(),
  role: z.string(),
});
export type CalendarUser = z.infer<typeof calendarUserSchema>;

// ============================================================================
// 6) Conteggi mensili — GET /calendar/appointments/month/:year/:month
//    e GET /calendar/unified/month/:year/:month
// ============================================================================
// getMonthAppointments (calendar.service:2183) usa `COUNT(*) as count` SENZA
// ::int e assegna `counts[date] = row.count` (nessuna conversione JS): sul wire
// il valore è STRINGA (pg numeric). Modelliamo union string|number.
export const calendarMonthCountsSchema = z.record(
  z.string(), // chiave YYYY-MM-DD
  z.union([z.string(), z.number()]), // pg COUNT(*) = stringa sul wire
);
export type CalendarMonthCounts = z.infer<typeof calendarMonthCountsSchema>;

// getUnifiedMonthCounts (calendar.service:2572) idem: ogni sotto-conteggio è
// `row.count` da COUNT(*)/COUNT(DISTINCT) senza ::int → stringa sul wire.
export const calendarUnifiedMonthCountsSchema = z.record(
  z.string(), // chiave YYYY-MM-DD
  z.object({
    appointments: z.union([z.string(), z.number()]),
    tasks: z.union([z.string(), z.number()]),
    videocalls: z.union([z.string(), z.number()]),
    workgroup_events: z.union([z.string(), z.number()]),
  }),
);
export type CalendarUnifiedMonthCounts = z.infer<typeof calendarUnifiedMonthCountsSchema>;

// ============================================================================
// 7) Stato sync Google — GET /calendar/google-sync/status
// ============================================================================
// stats via `COUNT(*) FILTER (...)` SENZA ::int (calendar.routes:1321-1324):
// wire = stringhe → union string|number. Account: SELECT id, account_email,
// account_label (nullable dalle colonne integration_templates).
export const calendarGoogleSyncStatusSchema = z.object({
  enabled: z.boolean(),
  google_connected: z.boolean(),
  google_accounts: z.array(
    z.object({
      id: z.string(),
      account_email: z.string().nullable(),
      account_label: z.string().nullable(),
    }),
  ),
  stats: z.object({
    // COUNT(*) FILTER senza ::int → stringa sul wire.
    synced: z.union([z.string(), z.number()]),
    pending: z.union([z.string(), z.number()]),
    errors: z.union([z.string(), z.number()]),
    total_linked: z.union([z.string(), z.number()]),
  }),
});
export type CalendarGoogleSyncStatus = z.infer<typeof calendarGoogleSyncStatusSchema>;

// ============================================================================
// 8) Contatore inbox — GET /calendar/inbox/count
// ============================================================================
// SQL `COUNT(*)::int AS count` (calendar.routes:2165/2170) → numero sul wire.
export const calendarInboxCountSchema = z.object({
  count: z.number(),
});
export type CalendarInboxCount = z.infer<typeof calendarInboxCountSchema>;

// ============================================================================
// 9) Gruppo turni — GET /hr/shifts/by-date (payload data[YYYY-MM-DD] = gruppi[])
// ============================================================================
// Shape costruita dal route (hr.routes:309-353) a partire da getShiftsByDateRange
// (hr.service:134-142). start_time/end_time sono TIME string (non ISO) da
// hr_shift_templates. people[] da users (join, NOT NULL nel SELECT).
export const hrShiftGroupSchema = z.object({
  shift_template_id: z.string(),
  shift_name: z.string(), // st.name
  shift_code: z.string(), // st.code
  start_time: z.string(), // time HH:MM:SS (non ISO)
  end_time: z.string(),
  shift_color: z.string(), // st.color
  is_overnight: z.boolean(),
  store_name: z.string(), // s.name (JOIN NOT NULL: store_id è FK obbligatoria)
  store_city: z.string(), // s.city
  people: z.array(
    z.object({
      user_id: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string(),
    }),
  ),
});
export type HrShiftGroup = z.infer<typeof hrShiftGroupSchema>;

// ============================================================================
// 10) Record presenza — GET /hr/attendance/records
// ============================================================================
// `SELECT ar.*` da hr_attendance_records (schema.sql:13855) + alias joinati
// (store_name, first_name/last_name/email, shift_name/shift_start/shift_end —
// hr.service:738-746). .passthrough() per colonne future.
export const hrAttendanceRecordSchema = z
  .object({
    // --- colonne hr_attendance_records (ar.*) ---
    id: z.string(),
    tenant_id: z.string(),
    user_id: z.string(),
    shift_assignment_id: z.string().nullable(),
    store_id: z.string(),
    // date (senza tz) → stringa 'YYYY-MM-DD' o Date lato service.
    date: z.union([z.string(), z.date()]),
    // timestamptz → ISO string sul wire.
    clock_in: z.union([z.string(), z.date()]),
    clock_out: z.union([z.string(), z.date()]).nullable(),
    break_start: z.union([z.string(), z.date()]).nullable(),
    break_end: z.union([z.string(), z.date()]).nullable(),
    total_work_minutes: z.number().nullable(),
    total_break_minutes: z.number().nullable(),
    status: z.string(),
    clock_in_method: z.string(),
    qr_token_hash: z.string().nullable(),
    // inet → stringa sul wire (nullable).
    ip_address: z.string().nullable(),
    user_agent: z.string().nullable(),
    // double precision → numero (nullable).
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    distance_meters: z.number().nullable(),
    is_out_of_shift: z.boolean(),
    notes: z.string().nullable(),
    created_at: z.union([z.string(), z.date()]),
    updated_at: z.union([z.string(), z.date()]),
    // --- alias joinati (LEFT JOIN → nullable) ---
    store_name: z.string().nullable(), // stores.name
    first_name: z.string().nullable(), // users.first_name
    last_name: z.string().nullable(), // users.last_name
    email: z.string().nullable(), // users.email
    shift_name: z.string().nullable(), // hr_shift_templates.name
    shift_start: z.string().nullable(), // time HH:MM:SS
    shift_end: z.string().nullable(),
  })
  .passthrough();
export type HrAttendanceRecord = z.infer<typeof hrAttendanceRecordSchema>;

// ───── Form appuntamenti: upload asset (editor form) ─────────────────────────

/**
 * `POST /calendar/forms/upload-logo` (multipart `file`) → `data`: url servita
 * da `/api/landing-pages/images/:id` più dimensione e mimetype rilevato.
 */
export const appointmentFormLogoUploadSchema = z.object({
  url: z.string(),
  size: z.number(),
  mimetype: z.string(),
});
export type AppointmentFormLogoUpload = z.infer<typeof appointmentFormLogoUploadSchema>;

/**
 * `POST /users/site-photo` (multipart `file` + `user_id`) → `data`: contratto
 * letto dall'editor form per la foto consulente. NB: al 2026-11 la rotta non
 * risulta montata nel backend (l'upload fallisce con 404): il DTO fissa la
 * shape attesa dal FE in vista dell'implementazione.
 */
export const userSitePhotoUploadSchema = z.object({
  site_photo_url: z.string(),
});
export type UserSitePhotoUpload = z.infer<typeof userSitePhotoUploadSchema>;
